/*
 * Carbisdale - Menu-Modul
 *
 * Verwaltet die Command-Palette und alle Menüs.
 * Unterstützt Keyboard-Navigation (Pfeiltasten, Buchstaben, Enter, Escape).
 *
 * Verantwortlichkeiten:
 * - Menü-State verwalten (offen/geschlossen, aktuelles Menü, Selektion)
 * - Keyboard-Events verarbeiten
 * - Overlay mit Menü-Inhalt füllen
 * - Actions auslösen
 */

import { MENU_STRUCTURE, APP_INFO, Style } from './config.js'
import { blurKeyboardSink } from './touchKeyboard.js'

/**
 * Menu-Klasse
 *
 * Verwaltet den kompletten Menü-State und die Navigation.
 * Kommuniziert über Callbacks mit der Hauptanwendung.
 */
export class Menu {
    /**
     * Erstellt ein neues Menu-System.
     *
     * @param {Overlay} overlay - Der Overlay für die Darstellung
     * @param {Object} callbacks - Callback-Funktionen für Actions
     */
    constructor(overlay, callbacks) {
        this.overlay = overlay
        this.callbacks = callbacks

        // Menü-State
        this.isOpen = false
        this.currentMenu = null      // Aktuelle Menü-Definition
        this.selectedIndex = 0       // Selektierter Eintrag

        // Position für Menüs (oben links)
        this.menuX = 1
        this.menuY = 1

        // Bounds des aktuellen Menüs (für Maus-Klicks)
        this.menuBounds = null

        // Callback für dynamische Menü-Items (z.B. Statistiken)
        this.dynamicItemsCallback = null
    }

    /**
     * Setzt den Callback für dynamische Menü-Items.
     * @param {Function} callback - (menuId) => [{key, label, ...}]
     */
    setDynamicItemsCallback(callback) {
        this.dynamicItemsCallback = callback
    }

    /**
     * Öffnet das Hauptmenü (Command-Palette).
     */
    open() {
        // Phase 6.0d: Soft-Keyboard schliessen, falls offen — sonst deckt sie
        // das Menue ggf. ab.
        blurKeyboardSink()
        this.isOpen = true
        this.currentMenu = MENU_STRUCTURE
        this.selectedIndex = 0
        this._render()
    }

    /**
     * Schließt das Menü komplett.
     */
    close() {
        this.isOpen = false
        this.currentMenu = null
        this.selectedIndex = 0
        this.menuBounds = null
        this.overlay.deactivate()
    }

    /**
     * Zeigt die About/Copyright Box an.
     */
    showAbout() {
        this.isOpen = true
        this.currentMenu = { id: 'about', isInfoBox: true }

        this.overlay.clear()
        this.overlay.drawPhosphorBox([
            APP_INFO.name,
            APP_INFO.subtitle,
            '',
            APP_INFO.copyright
        ], true)

        this.overlay.markDirty()
    }

    /**
     * Verarbeitet einen Tastendruck.
     * Gibt true zurück wenn das Event behandelt wurde.
     *
     * @param {KeyboardEvent} e - Das Keyboard-Event
     * @returns {boolean} Event wurde behandelt?
     */
    handleKeydown(e) {
        if (!this.isOpen) return false

        // Help-Box: jede Taste schließt sie
        if (this.currentMenu?.isInfoBox) {
            this.close()
            return true
        }

        const key = e.key

        // Navigation
        switch (key) {
            case 'Escape':
            case 'ArrowLeft':
                this._handleBack()
                return true

            case 'ArrowUp':
                this._moveSelection(-1)
                return true

            case 'ArrowDown':
                this._moveSelection(1)
                return true

            case 'Enter':
            case 'ArrowRight':
                this._selectCurrent()
                return true

            default:
                // Buchstaben-Shortcut
                if (key.length === 1) {
                    this._handleShortcut(key.toUpperCase())
                    return true
                }
        }

        return true  // Alle Tasten im Menü abfangen
    }

    /**
     * Bewegt die Selektion um delta Einträge.
     * Überspringt isDisabled, isInfo und isDivider Items.
     *
     * @param {number} delta - Bewegungsrichtung (-1 oder +1)
     */
    _moveSelection(delta) {
        const items = this._getCurrentItems()
        if (!items || items.length === 0) return

        const itemCount = items.length
        let newIndex = this.selectedIndex
        let attempts = 0

        // Nächsten selektierbaren Eintrag finden
        do {
            newIndex = (newIndex + delta + itemCount) % itemCount
            attempts++
        } while (
            attempts < itemCount &&
            (items[newIndex].isDisabled || items[newIndex].isInfo || items[newIndex].isDivider)
        )

        this.selectedIndex = newIndex
        this._render()
    }

    /**
     * Gibt die aktuellen Menü-Items zurück (ggf. dynamisch generiert).
     * @returns {Array|null}
     */
    _getCurrentItems() {
        if (!this.currentMenu) return null

        // Dynamische Menüs: Items zur Laufzeit generieren
        if (this.currentMenu.isDynamic && this.dynamicItemsCallback) {
            return this.dynamicItemsCallback(this.currentMenu.id)
        }

        return this.currentMenu.items
    }

    /**
     * Aktiviert den aktuell selektierten Eintrag.
     */
    _selectCurrent() {
        const items = this._getCurrentItems()
        if (!items) return

        const item = items[this.selectedIndex]
        this._activateItem(item)
    }

    /**
     * Behandelt einen Buchstaben-Shortcut.
     *
     * @param {string} key - Der gedrückte Buchstabe (uppercase)
     */
    _handleShortcut(key) {
        const items = this._getCurrentItems()
        if (!items) return

        const index = items.findIndex(item => item.key === key)
        if (index !== -1) {
            this.selectedIndex = index
            this._activateItem(items[index])
        }
    }

    /**
     * Aktiviert einen Menü-Eintrag.
     *
     * @param {Object} item - Der Menü-Eintrag
     */
    _activateItem(item) {
        // Disabled oder Info-Items ignorieren
        if (item.isDisabled || item.isInfo || item.isDivider) {
            return
        }

        // Hat Submenu? → Öffnen
        if (item.submenu) {
            this.currentMenu = item.submenu
            this.selectedIndex = this._findFirstSelectableIndex()
            this._render()
            return
        }

        // Hat Action? → Ausführen
        if (item.action) {
            // Info-Box (About): zeigt eigenen Overlay, daher Menue NICHT
            // vorher schliessen — close() wuerde den Box-Overlay loeschen.
            if (item.action === 'showAbout') {
                this._executeAction(item.action, item.value)
            } else {
                // WICHTIG: Erst Menü schließen, DANN Action ausführen!
                // Sonst würde close() den vom Dialog gezeichneten Overlay löschen.
                this.close()
                this._executeAction(item.action, item.value)
            }
        }
    }

    /**
     * Findet den ersten selektierbaren Index im aktuellen Menü.
     * @returns {number}
     */
    _findFirstSelectableIndex() {
        const items = this._getCurrentItems()
        if (!items) return 0

        for (let i = 0; i < items.length; i++) {
            if (!items[i].isDisabled && !items[i].isInfo && !items[i].isDivider) {
                return i
            }
        }
        return 0
    }

    /**
     * Führt eine Action aus.
     *
     * @param {string} action - Name der Action
     * @param {*} value - Optionaler Wert
     */
    _executeAction(action, value) {
        const callback = this.callbacks[action]
        if (callback) {
            callback(value)
        } else {
            console.warn(`Unbekannte Action: ${action}`)
        }
    }

    /**
     * Behandelt "Zurück" (Escape oder Pfeil links).
     */
    _handleBack() {
        // Im Hauptmenü → Schließen
        if (this.currentMenu?.id === 'main') {
            this.close()
            return
        }

        // In Submenu → Zurück zum Hauptmenü
        this.currentMenu = MENU_STRUCTURE
        this.selectedIndex = 0
        this._render()
    }

    /**
     * Rendert das aktuelle Menü in den Overlay.
     */
    _render() {
        const items = this._getCurrentItems()
        if (!items) return

        this.overlay.clear()

        // Aktuellen Wert ermitteln für Checkmark
        const currentValue = this._getCurrentValueForMenu()

        // Menü-Items aufbereiten. `valueAccessor` erlaubt es, fuer einzelne
        // Toggle-Items einen eigenen Bool-Status (Checkmark) abzufragen,
        // ohne den globalen `currentValue` des Submenus zu missbrauchen.
        // `labelAccessor` erlaubt dynamische Labels (z.B. "Fallback English"
        // vs. "Fallback Deutsch" je nach aktiver Hauptsprache).
        const menuItems = items.map(item => {
            let isCurrent = false
            if (item.valueAccessor && this.callbacks[item.valueAccessor]) {
                try { isCurrent = !!this.callbacks[item.valueAccessor]() }
                catch { isCurrent = false }
            }
            let label = item.label
            if (item.labelAccessor && this.callbacks[item.labelAccessor]) {
                try {
                    const dynamic = this.callbacks[item.labelAccessor]()
                    if (typeof dynamic === 'string' && dynamic.length > 0) label = dynamic
                } catch { /* statisches Label behalten */ }
            }
            return {
                key: item.key,
                label,
                hasSubmenu: !!item.submenu,
                value: item.value,
                isCurrent,
                isDisabled: item.isDisabled || false,
                isInfo: item.isInfo || false,
                isDivider: item.isDivider || false,
                isHeader: item.isHeader || false
            }
        })

        // Menü zeichnen und Bounds speichern
        const { width, height } = this._drawMenu(
            this.menuX,
            this.menuY,
            this.currentMenu.title,
            menuItems,
            this.selectedIndex,
            currentValue
        )

        // Bounds für Maus-Klicks speichern
        this.menuBounds = {
            x: this.menuX,
            y: this.menuY,
            width,
            height,
            itemCount: menuItems.length
        }

        this.overlay.markDirty()
    }

    /**
     * Rendert ein Menue-Overlay mit Frame + Titel + Items.
     *
     * War historisch in `js/overlay.js` als `drawMenu`, ist aber reine
     * Command-Palette-Logik — gehoert hierher. Nutzt die Overlay-Primitive
     * `drawMenuFrame`, `drawMenuItem`, `addTextSpan`, `setChar`.
     *
     * @param {number} x - Position X
     * @param {number} y - Position Y
     * @param {string} title - Menue-Titel
     * @param {Array} items - Menue-Eintraege [{key, label, hasSubmenu, isDisabled, isInfo, ...}]
     * @param {number} selectedIndex - Index des selektierten Eintrags
     * @param {*} currentValue - Aktueller Wert (fuer Checkmark)
     * @returns {{width: number, height: number}} Dimensionen
     */
    _drawMenu(x, y, title, items, selectedIndex = 0, currentValue = null) {
        // Breite berechnen: laengster Eintrag + Padding + Key + Pfeil.
        // Info-Items mit Two-Column-Layout (label + value, getrennte Spans)
        // brauchen die Summe label+value als Breite — sonst rutscht im
        // Mono-Mode der rechtsbuendige Value gegen den Label.
        const itemLengths = items.map(item => {
            // Divider haben kein Label — sie beeinflussen die Breite nicht.
            if (item.isDivider) return 0
            const labelLen = (item.label || '').length
            if (item.isInfo && item.value !== undefined) {
                return labelLen + 2 + String(item.value).length
            }
            return labelLen + (item.hasSubmenu ? 4 : 0)
        })
        const maxLabelLength = Math.max(title.length, ...itemLengths)
        const width = maxLabelLength + 8  // [K] Label → + Padding
        const height = items.length + 2   // Items + Rahmen oben/unten

        // Frame + Title via Overlay-Primitive
        this.overlay.drawMenuFrame(
            { x, y, width, height },
            { title, double: false, borderStyle: Style.MENU_BORDER, bgStyle: Style.MENU_BACKGROUND }
        )

        // Item-Bereich: linke Inset = 1 Cell Frame-Padding (analog Item-Marker
        // im alten Code, das `[K]` startete bei x + 1). itemWidth umfasst die
        // gesamte Innenbreite minus links/rechts Padding.
        const itemX = x + 1
        const itemWidth = width - 2

        items.forEach((item, index) => {
            const itemY = y + 1 + index

            // Divider-Item: volle horizontale Trennlinie ueber die gesamte
            // Innenbreite. Mono-Pfad reicht — die Linie ist 1-Cell hoch.
            if (item.isDivider) {
                for (let i = 1; i < width - 1; i++) {
                    this.overlay.setChar(x + i, itemY, '─', Style.MENU_BORDER)
                }
                return
            }

            // Info-Items: gedimmt, niemals selektiert. drawMenuItem behandelt
            // nur disabled — Info wird hier inline gerendert, proportional
            // im Prop-Mode (addTextSpan-Pfad).
            if (item.isInfo) {
                if (item.isHeader) {
                    // Header-Style: Title-Farbe, ueber gesamte Innenbreite
                    this.overlay.addTextSpan(itemX + 1, itemY, item.label, Style.PANE_TITLE, itemWidth - 2)
                    return
                }
                if (item.value !== undefined) {
                    // Two-Column: Label links, Value rechtsbuendig. Beide
                    // gehen ueber dieselbe Breite — Value rechts-aligned
                    // sitzt zwangslaeufig ueber dem Label-Span, aber da der
                    // Renderer Spans uebereinander legt und nur die richtige
                    // Zelle ueberlappt zeichnet, bleibt das visuell sauber.
                    // Stats-Items haben keine [K]-Brackets — Label sitzt am
                    // 1-Cell-Frame-Padding analog zum Header.
                    this.overlay.addTextSpan(itemX + 1, itemY, item.label, Style.PANE_INFO, itemWidth - 2)
                    this.overlay.addTextSpan(itemX + 1, itemY, String(item.value), Style.MENU_ITEM, itemWidth - 2, { align: 'right' })
                    return
                }
                this.overlay.addTextSpan(itemX + 1, itemY, item.label, Style.PANE_INFO, itemWidth - 2)
                return
            }

            // isCurrent kann implizit ueber den value/currentValue-Vergleich
            // kommen — Mapping erfolgt hier, drawMenuItem liest nur das Flag.
            const isCurrent = item.isCurrent || item.value === currentValue
            const isSelected = index === selectedIndex && !item.isDisabled

            this.overlay.drawMenuItem(
                itemX, itemY,
                {
                    key: item.key,
                    label: item.label,
                    hasSubmenu: !!item.hasSubmenu,
                    isCurrent: !!isCurrent,
                    isDisabled: !!item.isDisabled
                },
                { selected: isSelected, itemWidth }
            )
        })

        return { width, height }
    }

    /**
     * Ermittelt den aktuellen Wert für das aktuelle Menü (für Checkmark).
     *
     * @returns {*} Aktueller Wert oder null
     */
    _getCurrentValueForMenu() {
        if (!this.currentMenu?.id) return null

        switch (this.currentMenu.id) {
            case 'display':
                return this.callbacks.getDisplayMode?.()
            case 'spellcheck':
                return this.callbacks.getSpellcheckLang?.()
            default:
                return null
        }
    }

    /**
     * Prüft ob das Menü offen ist.
     *
     * @returns {boolean}
     */
    isMenuOpen() {
        return this.isOpen
    }

    /**
     * Prüft ob eine Position innerhalb des Menüs liegt.
     *
     * @param {number} charX - Spalte
     * @param {number} charY - Zeile
     * @returns {boolean}
     */
    isInsideMenu(charX, charY) {
        if (!this.menuBounds) return false

        const b = this.menuBounds
        return charX >= b.x && charX < b.x + b.width &&
               charY >= b.y && charY < b.y + b.height
    }

    /**
     * Behandelt Maus-Bewegung (Hover).
     * Gibt true zurück wenn über dem Menü, false sonst.
     *
     * @param {number} charX - Spalte
     * @param {number} charY - Zeile
     * @returns {boolean}
     */
    handleMouseMove(charX, charY) {
        if (!this.isOpen || !this.menuBounds) return false

        // Help-Box: keine Hover-Effekte
        if (this.currentMenu?.isInfoBox) {
            return this.isInsideMenu(charX, charY)
        }

        // Nicht über Menü: kein Hover
        if (!this.isInsideMenu(charX, charY)) return false

        const b = this.menuBounds

        // Auf Rahmen: keine Änderung
        if (charY === b.y || charY === b.y + b.height - 1) {
            return true
        }

        // Über Menü-Eintrag: Selektion ändern. Nicht-selektierbare Items
        // (Divider, Info, Disabled) sollen weder Hover-Highlight bekommen
        // noch die aktuelle Selektion verschieben — sonst springt der
        // Selection-Stripe auf ein optisch leeres Element.
        const itemIndex = charY - b.y - 1
        const items = this._getCurrentItems()
        const hoverItem = items && items[itemIndex]
        const unselectable = hoverItem && (
            hoverItem.isDisabled || hoverItem.isInfo || hoverItem.isDivider
        )

        if (itemIndex >= 0 && itemIndex < b.itemCount
            && itemIndex !== this.selectedIndex
            && !unselectable
        ) {
            this.selectedIndex = itemIndex
            this._render()
        }

        return true
    }

    /**
     * Behandelt einen Maus-Klick.
     * Gibt true zurück wenn der Klick behandelt wurde.
     *
     * @param {number} charX - Spalte
     * @param {number} charY - Zeile
     * @returns {boolean}
     */
    handleClick(charX, charY) {
        if (!this.isOpen) return false

        // Help-Box: Klick irgendwo schließt sie
        if (this.currentMenu?.isInfoBox) {
            this.close()
            return true
        }

        // Klick außerhalb des Menüs: schließen
        if (!this.isInsideMenu(charX, charY)) {
            this.close()
            return true
        }

        // Klick auf Rahmen ignorieren
        const b = this.menuBounds
        if (charY === b.y || charY === b.y + b.height - 1) {
            return true  // Behandelt, aber keine Aktion
        }

        // Klick auf Menü-Eintrag: Index berechnen und aktivieren. Bei
        // Klick auf nicht-selektierbare Items (Divider, Info, Disabled) wird
        // selectedIndex NICHT umgesetzt — sonst landet die Selektion auf
        // einem leeren Element und der naechste Pfeil verhaelt sich seltsam.
        const itemIndex = charY - b.y - 1  // -1 für oberen Rahmen
        const items = this._getCurrentItems()
        const clickItem = items && items[itemIndex]
        const unselectable = clickItem && (
            clickItem.isDisabled || clickItem.isInfo || clickItem.isDivider
        )

        if (itemIndex >= 0 && itemIndex < b.itemCount && !unselectable) {
            this.selectedIndex = itemIndex
            this._selectCurrent()
        }

        return true
    }
}
